import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Switch, Route, Redirect, useLocation} from 'react-router-dom';
import styled from 'styled-components';
import {useQueryClient} from '@tanstack/react-query';

import OpportunityOverview from './opportunityOverview';
import DiscoverBookList from './discoverBookList';
import DiscoveryExperience from './DiscoveryExperience';
import {discoveryPath, parseDiscoveryState} from './discoveryState';
import {useFeatureFlags} from '../../hooks/useFeatureFlags';
import {setDiscoveryExperimentAssignment} from '../../modules/app';
import {exposeDiscoveryExperiment, DiscoveryExperimentContext,
  recordDiscoveryExperimentAction} from './experimentClient';

import {MediaQuery} from '../../constants/size';

const Wrapper = styled.div`
  display: flex;
  padding-bottom: 4em;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  ${MediaQuery.tablet} {
    max-width: calc(
      100vw - ${props => (props.sidebarVisible ? 250 : 0)}px - 2rem
    );
  }
  ${MediaQuery.phone} {
    max-width: calc(100vw - 2rem);
  }
`;

const BetaOpportunities = ({}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userProfile = useSelector(state => state.user.userProfile);
  const signedInUser = useSelector(state => state.user.user);
  const userProfileLoaded = useSelector(state => state.user.userProfileLoaded);
  const profileLoadError = useSelector(state => state.user.profileLoadError);
  const routeLocation = useLocation();
  const bookRedirect = /^\/discover\/[^/]+\/?$/.test(routeLocation.pathname);
  const defaultFontSize = useSelector(state => state.app.defaultFontSize);
  const sidebarVisible = useSelector(state => state.app.sidebarVisible);
  const experimentState = useSelector(state => state.app.discoveryExperimentAssignment);
  const {flags, isLoading: flagsLoading} = useFeatureFlags();
  useEffect(() => {
    const timer = window.setInterval(() => {queryClient.refetchQueries({
      queryKey: ['globalFeatureFlags']
    });}, 60_000);
    return () => window.clearInterval(timer);
  }, [queryClient]);
  const discoveryRedesign = flags?.discovery_redesign === true ||
    (flags?.discovery_redesign_beta_access_allowed === true &&
      userProfile?.betaFeatures?.discovery_redesign === true);
  const flagPhase = flags?.discovery_experiment === true &&
    flags?.discovery_experiment_aa !== true ? 'ab' :
    flags?.discovery_experiment_aa === true && flags?.discovery_experiment !== true ? 'aa' : null;
  const experimentEligible = Boolean(flagPhase) && !discoveryRedesign &&
    !bookRedirect && !signedInUser?.isAnonymous &&
    Boolean(userProfile?._id && userProfile._id === signedInUser?.uid);
  useEffect(() => {
    if (!experimentEligible) {
      dispatch(setDiscoveryExperimentAssignment(null));
      return undefined;
    }
    let cancelled = false;
    dispatch(setDiscoveryExperimentAssignment(null));
    exposeDiscoveryExperiment().then(result => {
      if (!cancelled) dispatch(setDiscoveryExperimentAssignment({uid: userProfile._id,
        requestedPhase: flagPhase,
        phase: result.active ? result.phase : null,
        variant: result.active ? result.variant : null}));
    }).catch(() => {if (!cancelled) dispatch(setDiscoveryExperimentAssignment({uid: userProfile._id,
      requestedPhase: flagPhase, phase: null, variant: null}));});
    return () => {cancelled = true;};
  }, [dispatch, experimentEligible, flagPhase, userProfile?._id]);
  const experimentReady = !experimentEligible ||
    (experimentState?.uid === userProfile?._id && experimentState?.requestedPhase === flagPhase);
  const awaitingProfile = Boolean(flagPhase) && !bookRedirect &&
    Boolean(signedInUser) && !userProfileLoaded && !profileLoadError;
  const experimentVariant = experimentReady ? experimentState?.variant : null;
  const inExperiment = experimentEligible &&
    (experimentVariant === 'control' || experimentVariant === 'treatment');
  const showRedesign = discoveryRedesign || (inExperiment &&
    experimentState?.phase === 'ab' && experimentVariant === 'treatment');

  const currentFontSize = userProfile?.readerSettings?.fontSize ?? defaultFontSize;

  return (
    <Wrapper
      sidebarVisible={sidebarVisible}
      className={`br-text font-size-${currentFontSize}`}>
      <DiscoveryExperimentContext.Provider value={inExperiment ? experimentState.phase : null}>
      {!bookRedirect && (flagsLoading || awaitingProfile || !experimentReady) ? null : <Switch>
        <Route
          exact
          path='/discover/:bookId'
          render={props => (
            <Redirect to={`/books/${props.match.params.bookId}`} />
          )}
        />
        <Route path='/discover/genre/:genre' render={props => showRedesign
          ? <Redirect to={discoveryPath({...parseDiscoveryState(props.location.search), view: 'results', genres: [decodeURIComponent(props.match.params.genre)]})} />
          : <DiscoverBookList {...props} />} />
        <Route path='/discover' render={props => showRedesign
          ? <DiscoveryExperience {...props} />
          : <OpportunityOverview {...props} onExperimentAction={action => {
            if (inExperiment) recordDiscoveryExperimentAction(action, experimentState.phase);
          }} />} />
      </Switch>}
      </DiscoveryExperimentContext.Provider>
    </Wrapper>
  );
};

export default BetaOpportunities;
